import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { fadeUp, viewportOnce } from "../../lib/motion";
import { HERO_IMAGES, IMG_STONE_DUSK } from "../../lib/product";
import { SectionIntro, WireframeHouse } from "./shared";

const FRAMES = [
    {
        code: "STEP 01",
        label: "2D PLAN",
        img: HERO_IMAGES.plan,
        alt: "Architectural floor plan of a residence being drawn",
        overlay: null,
    },
    {
        code: "STEP 02",
        label: "3D HOUSE",
        img: HERO_IMAGES.house3d,
        alt: "Three-dimensional model view of the same contemporary home",
        overlay: <WireframeHouse className="absolute inset-x-6 bottom-4 w-[52%] text-white/40" />,
    },
    {
        code: "STEP 03",
        label: "REALISTIC VISUAL",
        img: IMG_STONE_DUSK,
        alt: "Realistic evening visual of the finished contemporary home",
        overlay: null,
    },
];

const HomeVisionShowcase = () => (
    <section
        id="home-vision"
        data-testid="home-vision-showcase-section"
        className="relative bg-ink py-24 lg:py-32"
    >
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-16">
            <SectionIntro
                testid="homevision-intro"
                eyebrow="HOME VISION"
                title="See your home before you build it."
                copy="Turn your architectural plan into an AI-generated visual concept that helps you explore the possibilities before construction begins."
            />

            <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                data-testid="homevision-board"
                className="mt-14 grid items-stretch gap-6 lg:grid-cols-[1fr_auto_1fr_auto_1fr]"
            >
                {FRAMES.map((frame, i) => (
                    <div key={frame.code} className="contents">
                        {i > 0 && (
                            <div className="flex items-center justify-center lg:flex-col">
                                <span className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-mist">
                                    <ArrowDown className="h-4 w-4 lg:hidden" />
                                    <ArrowDown className="hidden h-4 w-4 lg:block" />
                                </span>
                            </div>
                        )}
                        <figure className="group relative flex flex-col border border-white/12 bg-[#0C1428]">
                            <div className="relative flex-1 overflow-hidden">
                                <img
                                    src={frame.img}
                                    alt={frame.alt}
                                    loading="lazy"
                                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03] lg:aspect-auto lg:h-full lg:min-h-[300px]"
                                />
                                {frame.overlay}
                            </div>
                            <figcaption className="flex items-center justify-between border-t border-white/10 px-4 py-3">
                                <span className="font-mono text-[13px] tracking-[0.22em] text-white">
                                    {frame.label}
                                </span>
                                <span className="font-mono text-[13px] tracking-[0.22em] text-cad">
                                    {frame.code}
                                </span>
                            </figcaption>
                        </figure>
                    </div>
                ))}
            </motion.div>

            <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                className="mt-8 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end"
            >
                <p className="max-w-xl text-[13px] leading-relaxed text-mist/70">
                    AI visualisation is a concept representation and is not a
                    construction drawing, approved architectural documentation
                    or engineering documentation.
                </p>
            </motion.div>
        </div>
    </section>
);

export default HomeVisionShowcase;
